import DEFAULTS from "./defaults";
import TEMPLATE from "./template";
import render from "./render";
import events from "./events";
import handlers from "./handlers";
import methods from "./methods";
import others from "./others";
import {
  BUTTONS,
  CLASS_SVG_CLOSE,
  CLASS_FADE,
  CLASS_FIXED,
  CLASS_FULLSCREEN,
  CLASS_HIDE,
  CLASS_INVISIBLE,
  DATA_ACTION,
  EVENT_CLICK,
  EVENT_LOAD,
  EVENT_READY,
  NAMESPACE,
  REGEXP_SPACES,
  WINDOW
} from "./constants";
import {
  addClass,
  addListener,
  assign,
  dispatchEvent,
  forEach,
  getResponsiveClass,
  hyphenate,
  isFunction,
  isNumber,
  isPlainObject,
  isString,
  isUndefined,
  removeListener,
  setData,
  setStyle,
  toggleClass,
  isElement
} from "./utilities";

const AnotherViewer = WINDOW.Viewer;

/**
 * Viewer构造函数
 */
class Viewer {
  /**
   * 创建 Viewer.
   * @param {Element} element - The target element for viewing.
   * @param {Object} [options={}] - The configuration options.
   */
  constructor(element, options = {}) {
    // if (!element || element.nodeType !== 1) {
    //   throw new Error('The first argument is required and must be an element.')
    // }
    if (!element && !options.datas) {
      throw new Error("element or options.datas is required");
    }
    if (!element) {
      element = document.createElement("div");
      element.isElement = false;
    } else if (isElement(element)) {
      element.isElement = true;
    } else {
      throw new Error("element or options.datas is required");
    }
    element.datas = options.datas;

    this.element = element;
    this.options = assign({}, DEFAULTS, isPlainObject(options) && options);
    this.action = false;
    this.fading = false;
    this.fulled = false;
    this.hiding = false;
    this.imageClicked = false;
    this.imageData = {};
    this.index = this.options.initialViewIndex;
    this.isImg = false;
    this.isShown = false; // 是否显示
    this.length = 0;
    this.played = false;
    this.playing = false;
    this.pointers = {};
    this.ready = false;
    this.showing = false;
    this.timeout = false;
    this.tooltipping = false;
    this.viewed = false;
    this.viewing = false; // 正在查看的
    this.wheeling = false;
    this.zooming = false;
    this.init();
  }

  /**
   * 初始化方法
   */
  init() {
    const { element, options } = this;

    if (element[NAMESPACE]) {
      return;
    }

    // 给当前元素添加变量等于当前对象
    element[NAMESPACE] = this;

    // element元素是否是img标签
    const isImg = element.isElement ? element.tagName.toLowerCase() === "img" : false;
    const images = [];

    // eslint-disable-next-line no-nested-ternary
    forEach(isImg ? [element] : element.isElement ? element.querySelectorAll("img") : element.datas, (image) => {
      if (isFunction(options.filter)) { // 对外暴露filter方法
        if (options.filter.call(this, image)) { // 是否需要显示
          images.push(image);
        }
      } else if (this.getImageURL(image)) { // 获取url是否存在，默认是src获取，如果是方法，则调用方法
        images.push(image);
      }
    });

    this.isImg = isImg;
    this.length = images.length;
    this.images = images;
    this.initBody(); // 初始化body，并赋值到this.body

    // Override `transition` option if it is not supported
    if (isUndefined(document.createElement(NAMESPACE).style.transition)) {
      options.transition = false;
    }

    // 如果是el中显示
    if (options.inline) {
      let count = 0;
      const progress = () => {
        count += 1;

        if (count === this.length) {
          // eslint-disable-next-line prefer-const
          let timeout;

          this.initializing = false;
          this.delaying = {
            abort: () => {
              clearTimeout(timeout);
            }
          };

          // build asynchronously to keep `this.viewer` is accessible in `ready` event handler.
          timeout = setTimeout(() => {
            this.delaying = false;
            this.build();
          }, 0);
        }
      };

      this.initializing = {
        abort: () => {
          forEach(images, (image) => {
            if (!image.complete) {
              removeListener(image, EVENT_LOAD, progress);
            }
          });
        }
      };

      forEach(images, (image) => {
        if (image.complete) {
          progress();
        } else {
          addListener(image, EVENT_LOAD, progress, {
            once: true
          });
        }
      });
    } else {
      // 通过事件代理模式监听img点击
      addListener(element, EVENT_CLICK, (this.onStart = ({ target }) => {
        if (target.tagName.toLowerCase() === "img" &&
          (!isFunction(options.filter) || options.filter.call(this, target))) {
          this.view(this.images.indexOf(target));
        }
      }));
    }
  }

  build() {
    if (this.ready) {
      return;
    }

    const { element, options } = this;
    const parent = element.parentNode;
    const template = document.createElement("div");

    template.innerHTML = TEMPLATE;

    const viewer = template.querySelector(`.${NAMESPACE}-container`);
    const title = viewer.querySelector(`.${NAMESPACE}-title`);
    const toolbar = viewer.querySelector(`.${NAMESPACE}-toolbar`);
    const navbarWrapper = viewer.querySelector(`.${NAMESPACE}-navbar-wrapper`);
    const navbar = viewer.querySelector(`.${NAMESPACE}-navbar`);
    const button = viewer.querySelector(`.${NAMESPACE}-button`);
    const canvas = viewer.querySelector(`.${NAMESPACE}-canvas`);
    const listLeft = viewer.querySelector(`.${NAMESPACE}-list-left`);
    const listRight = viewer.querySelector(`.${NAMESPACE}-list-right`);

    this.parent = parent;
    this.viewer = viewer;
    this.title = title;
    this.navbarWrapper = navbarWrapper;
    this.toolbar = toolbar;
    this.listLeft = listLeft;
    this.listRight = listRight;
    this.navbar = navbar;
    this.button = button;
    this.canvas = canvas;
    this.footer = viewer.querySelector(`.${NAMESPACE}-footer`);
    this.tooltipBox = viewer.querySelector(`.${NAMESPACE}-tooltip`);
    this.player = viewer.querySelector(`.${NAMESPACE}-player`);
    this.list = viewer.querySelector(`.${NAMESPACE}-list`);

    // 是否显示标题
    addClass(title, !options.title ? CLASS_HIDE : getResponsiveClass(Array.isArray(options.title)
      ? options.title[0]
      : options.title));
    // 是否显示底部图片列表导航
    addClass(navbar, !options.navbar ? CLASS_HIDE : getResponsiveClass(options.navbar));
    // 是否显示右上角按钮
    toggleClass(button, CLASS_HIDE, !options.button);

    // 是否点击背景的时候响应事件
    if (options.backdrop) {
      addClass(viewer, `${NAMESPACE}-backdrop`);

      if (!options.inline && options.backdrop !== "static") {
        setData(canvas, DATA_ACTION, "hide");
      }
    } else {
      addClass(viewer, `${NAMESPACE}-backdrop`);
    }

    setData(listLeft, DATA_ACTION, "left");
    setData(listRight, DATA_ACTION, "right");

    // 给viewer添加className
    if (isString(options.className) && options.className) {
      // In case there are multiple class names
      options.className.split(REGEXP_SPACES).forEach((className) => {
        addClass(viewer, className);
      });
    }

    // 工具栏处理
    if (options.toolbar) {
      const list = document.createElement("ul");
      const custom = isPlainObject(options.toolbar);
      const zoomButtons = BUTTONS.slice(0, 3);
      const rotateButtons = BUTTONS.slice(7, 9);
      const scaleButtons = BUTTONS.slice(9);

      if (!custom) {
        addClass(toolbar, getResponsiveClass(options.toolbar));
      }

      // 处理工具条
      forEach(custom ? options.toolbar : BUTTONS, (value, index) => {
        const deep = custom && isPlainObject(value);
        const name = custom ? hyphenate(index) : value;
        const show = deep && !isUndefined(value.show) ? value.show : value;

        if (
          !show ||
          (!options.zoomable && zoomButtons.indexOf(name) !== -1) ||
          (!options.rotatable && rotateButtons.indexOf(name) !== -1) ||
          (!options.scalable && scaleButtons.indexOf(name) !== -1)
        ) {
          return;
        }

        // const size = deep && !isUndefined(value.size) ? value.size : value;
        const click = deep && !isUndefined(value.click) ? value.click : value;

        if (name === "prev" || name === "next") {
          const temp = document.createElement("div");
          addClass(temp, `${NAMESPACE}-svg-${name}`);
          if (!isFunction(click)) {
            setData(temp, DATA_ACTION, name);
          }
          if (isFunction(click)) {
            addListener(temp, EVENT_CLICK, click);
          }
          this[`${name}El`] = temp;
          temp.innerHTML = `
            <svg aria-hidden="true" class="en-icon en-icon-prev-next"  style="width: 20px; height: 20px; color: #fff" data-viewer-action="${name}">
                <use data-viewer-action="${name}" xlink:href="#${name === "prev" ? "iconshangxiaqiehuan-wodehangcheng" : "iconshangxiaqiehuan-wodehangcheng2"}"></use>
            </svg>
         `;
          viewer.appendChild(temp);
          return;
        }

        const item = document.createElement("li");

        item.setAttribute("role", "button");
        addClass(item, `${NAMESPACE}-svg-${name}`);

        if (!isFunction(click)) {
          setData(item, DATA_ACTION, name);
        }

        if (isNumber(show)) {
          addClass(item, getResponsiveClass(show));
        }
        let icon = "";
        switch (name) {
          case "rotate-left":
            icon = "iconzuoxuan-chakantupian";
            break;
          case "rotate-right":
            icon = "iconyouxuan-chakantupian1";
            break;
          case "zoom-in":
            icon = "iconfangda-chakantupian";
            break;
          case "zoom-out":
            icon = "iconsuoxiao-chakantupian";
            break;
          default:
            break;
        }
        item.innerHTML = `
        <svg aria-hidden="true" class="en-icon"  style="width: 20px; height: 20px; color: #A9B5C6" data-viewer-action="${name}">
            <use data-viewer-action="${name}" xlink:href="#${icon}"></use>
        </svg>
        `;

        // if (["small", "large"].indexOf(size) !== -1) {
        //   addClass(item, `${NAMESPACE}-${size}`)
        // } else if (name === "play") {
        //   addClass(item, `${NAMESPACE}-large`)
        // }

        if (isFunction(click)) {
          addListener(item, EVENT_CLICK, click);
        }

        list.appendChild(item);
      });

      toolbar.appendChild(list);
    } else {
      addClass(toolbar, CLASS_HIDE);
    }

    if (!options.rotatable) {
      const rotates = toolbar.querySelectorAll("li[class*=\"rotate\"]");

      addClass(rotates, CLASS_INVISIBLE);
      forEach(rotates, (rotate) => {
        toolbar.appendChild(rotate);
      });
    }

    // inline模式
    if (options.inline) {
      addClass(button, CLASS_FULLSCREEN);
      setStyle(viewer, {
        zIndex: options.zIndexInline
      });

      if (window.getComputedStyle(parent).position === "static") {
        setStyle(parent, {
          position: "relative"
        });
      }

      parent.insertBefore(viewer, element.nextSibling);
    } else {
      addClass(button, CLASS_SVG_CLOSE);
      button.innerHTML = `
        <svg aria-hidden="true" class="en-icon"  style="width: 30px; height: 30px; color: #A9B5C6" data-viewer-action="mix">
            <use data-viewer-action="mix" xlink:href="#iconguanbi-danchuang"></use>
        </svg>
      `;
      addClass(viewer, CLASS_FIXED);
      addClass(viewer, CLASS_FADE);
      addClass(viewer, CLASS_HIDE);

      setStyle(viewer, {
        zIndex: options.zIndex
      });

      let { container } = options;

      if (isString(container)) {
        const ownerDocument = element.ownerDocument || document.body.ownerDocument;
        container = ownerDocument.querySelector(container);
      }

      if (!container) {
        container = this.body;
      }

      container.appendChild(viewer);
    }

    if (options.inline) {
      this.render();
      this.bind();
      this.isShown = true;
    }

    this.ready = true;

    if (isFunction(options.ready)) {
      addListener(element, EVENT_READY, options.ready, {
        once: true
      });
    }

    if (dispatchEvent(element, EVENT_READY) === false) {
      this.ready = false;
      return;
    }

    if (this.ready && options.inline) {
      this.view(this.index);
    }
  }

  /**
   * Get the no conflict viewer class.
   * @returns {Viewer} The viewer class.
   */
  static noConflict() {
    window.Viewer = AnotherViewer;
    return Viewer;
  }

  /**
   * Change the default options.
   * @param {Object} options - The new default options.
   */
  static setDefaults(options) {
    assign(DEFAULTS, isPlainObject(options) && options);
  }
}

assign(Viewer.prototype, render, events, handlers, methods, others);

export default Viewer;
